import { useMemo } from "react";
import { useAuthStore } from "@/stores/authStore";

/**
 * Hook to check permissions with user data from authStore (simplified)
 * @returns Object with permission checking functions and state
 */
export function useDynamicPermissions() {
  const {
    localUser,
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    checkPermissionRule,
  } = useAuthStore();

  // Memoize the result to avoid unnecessary re-renders
  return useMemo(
    () => ({
      // Permission checking functions
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      checkPermissionRule,

      // State
      userPermissions: permissions || [],
      isLoading: false,
      error: null,

      // User info
      userId: localUser?.id,
      userRole: localUser?.role?.name,
      userCompanyId: localUser?.companyId,
    }),
    [
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      checkPermissionRule,
      permissions,
      localUser?.id,
      localUser?.role,
      localUser?.companyId,
    ]
  );
}

/**
 * Hook for permission-based conditional rendering
 * @param permission Permission or rule to check
 * @returns Boolean indicating if user has permission
 */
export function useHasPermission(
  permission:
    | string
    | string[]
    | { anyOf?: string[]; allOf?: string[]; not?: string[] }
) {
  const { hasPermission, hasAnyPermission, checkPermissionRule, isLoading } =
    useDynamicPermissions();

  // Check permission based on type
  const allowed = useMemo(() => {
    if (isLoading) return false;

    if (typeof permission === "string") {
      return hasPermission(permission);
    } else if (Array.isArray(permission)) {
      return hasAnyPermission(permission);
    } else {
      return checkPermissionRule(permission);
    }
  }, [
    permission,
    hasPermission,
    hasAnyPermission,
    checkPermissionRule,
    isLoading,
  ]);

  return {
    allowed,
    isLoading,
  };
}

/**
 * Hook for company-scoped permission checking
 * @returns Object with company-aware permission functions
 */
export function useCompanyPermissions() {
  const { localUser, permissions } = useAuthStore();
  const { hasPermission } = useDynamicPermissions();

  return useMemo(
    () => ({
      // Check if user can manage a resource in a specific company
      canManageCompanyResource: (
        targetCompanyId: string | null | undefined,
        permission: string
      ): boolean => {
        if (!localUser?.role || !permissions) return false;

        // Super admin and platform admin can manage all resources
        if (
          localUser.role?.name === "super_admin" ||
          localUser.role?.name === "platform_admin"
        ) {
          return hasPermission(permission);
        }

        // Company admin and company user can only manage resources in their company
        if (
          localUser.role?.name === "company_admin" ||
          localUser.role?.name === "company_user"
        ) {
          if (!localUser.companyId || !targetCompanyId) return false;
          if (localUser.companyId !== targetCompanyId) return false;
          return hasPermission(permission);
        }

        // Regular user - check basic permission
        return hasPermission(permission);
      },

      // Check if user can view company data
      canViewCompanyData: (
        targetCompanyId: string | null | undefined
      ): boolean => {
        if (!localUser?.role) return false;

        // Super admin and platform admin can view all companies
        if (
          localUser.role?.name &&
          ["super_admin", "platform_admin"].includes(localUser.role.name)
        ) {
          return true;
        }

        // Company users can only view their own company
        if (
          localUser.role?.name &&
          ["company_admin", "company_user"].includes(localUser.role.name)
        ) {
          return localUser.companyId === targetCompanyId;
        }

        return false;
      },
    }),
    [localUser?.role, localUser?.companyId, permissions, hasPermission]
  );
}

/**
 * Hook to check route access with enhanced permission logic
 * @param permission Required permission for the route
 * @returns boolean indicating if user has access
 */
export function useCanAccessRoute(
  permission?:
    | string
    | string[]
    | { anyOf?: string[]; allOf?: string[]; not?: string[] }
): boolean {
  const { allowed } = useHasPermission(permission || "");

  return useMemo(() => {
    if (!permission) return true;
    return allowed;
  }, [permission, allowed]);
}
